import { Platform } from '@angular/cdk/platform';
import { isPlatformBrowser, NgClass } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  computed,
  effect,
  input,
  linkedSignal,
  output,
  signal,
  untracked
} from '@angular/core';
import { FormBuilder, FormsModule } from '@angular/forms';
import { MatDatepickerInput, MatDatepicker } from '@angular/material/datepicker';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'avn/modules/services/api.service';
import { GoogleMapsLoaderService } from 'avn/modules/services/google-maps-loader.service';
import { StoreService } from 'avn/modules/services/store.service';
import { toUrlSlug } from 'avn/modules/services/utils/url-slug.util';
import { InputSafetyDirective } from 'avn/modules/shared/directives/input-safety.directive';
import {
  IAvaanaCategory,
  Location,
  ILocationDropdownList,
  IPopularServiceDatum,
  Prediction
} from 'avn/modules/shared/interfaces/homepage';
import { HighlightPipe } from 'avn/modules/shared/pipes/highlight.pipe';
import { SlugTextPipe } from 'avn/modules/shared/pipes/slug-text.pipe';
import moment from 'moment';
import { debounceTime, Subject, Subscription } from 'rxjs';

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY'
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

@Component({
  selector: 'app-search-box-common',
  templateUrl: './search-box-common.html',
  styleUrls: ['./search-box-common.scss'],
  imports: [NgClass, InputSafetyDirective, FormsModule, MatDatepickerInput, MatDatepicker, HighlightPipe]
})
export class SearchBoxCommonComponent implements OnInit, OnDestroy {
  private readonly subscriptions: Subscription[] = [];
  private readonly documentClickListener = (event: MouseEvent) => {
    const dropdowns = document.querySelectorAll('.search-input-box');
    if (dropdowns) {
      let opened = false;
      dropdowns.forEach((dropdown: Element) => {
        if (dropdown && !dropdown.contains(event.target as Node)) {
          dropdown.classList.remove('show');
        }
        if (dropdown.classList.contains('show')) {
          opened = true;
        }
      });
      if (!opened) {
        this.dropdownType.set('');
      }
      this.cd.markForCheck();
    }
  };

  public readonly dropdownType = signal('');
  public todayDate = moment().format('YYYY-MM-DD');
  public readonly popularCategories = signal<IPopularServiceDatum[]>([]);
  public allServices: IAvaanaCategory[] = [];
  public readonly serviceText = signal('');
  public readonly locationText = signal('');
  public readonly searchType = signal('service');
  public locationTextChange = new Subject<string>();
  public readonly filteredLocationsList = signal<ILocationDropdownList[]>([]);
  public searchSlug = '';
  public locationSlug = '';
  public postCodeSlug = '';
  public readonly in_searchSlug = input<any>();
  public readonly in_claimNumber = input<any>();
  public readonly in_selectedDate = input<any>();
  public readonly in_locationText = input<any>();

  public readonly selectedDate = linkedSignal<any>(() => {
    const value = this.in_selectedDate();
    return value === undefined ? moment(this.todayDate) : value;
  });
  public readonly selectedDateText = linkedSignal(() => moment(this.selectedDate()).format('YYYY-MM-DD'));
  public readonly claimNumber = computed(() => this.in_claimNumber());
  public dateForm = this.fb.group({
    date: ['']
  });

  public browserTest: any;
  public extra_params: any;

  public selectedLocation: any;

  @ViewChild('locationInput') public locationInput!: ElementRef;

  public readonly closePop = output();

  public constructor(
    private readonly api: ApiService,
    private readonly store: StoreService,
    private readonly router: Router,
    private readonly slugText: SlugTextPipe,
    @Inject(PLATFORM_ID) private readonly platformId: any,
    private readonly platform: Platform,
    private readonly fb: FormBuilder,
    private readonly cd: ChangeDetectorRef,
    private readonly route: ActivatedRoute,
    private readonly googleMapsLoader: GoogleMapsLoaderService
  ) {
    effect(() => {
      const value = this.in_searchSlug();
      if (value) {
        untracked(() => this.applySearchSlug(value));
      }
    });
    effect(() => {
      const value = this.in_locationText();
      if (value !== undefined) {
        untracked(() => this.applyLocationText(value));
      }
    });
  }

  private applySearchSlug(value: any): void {
    this.searchSlug = value;
    this.serviceText.set(this.slugText.transform(value, 'capitalize'));
  }

  private applyLocationText(value: any): void {
    this.locationText.set(value);
    this.locationSlug = toUrlSlug(this.store.meraLocation.city_name);
    this.postCodeSlug = this.store.meraLocation.post_code.toLowerCase();
  }

  public ngOnInit(): void {
    this.extra_params = this.route.snapshot.queryParams;
    this.browserTest = this.platform;
    if (isPlatformBrowser(this.platformId)) {
      document.addEventListener('click', this.documentClickListener);
    }
    const locationTextSub = this.locationTextChange.pipe(debounceTime(500)).subscribe((value) => {
      this.getPlaces(value);
    });
    this.subscriptions.push(locationTextSub);

    this.fetchPopularCategories();
    this.fetchAllServices();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('click', this.documentClickListener);
    }
  }

  public readonly filteredPopularCategories = computed(() => {
    const serviceText = this.serviceText();
    const popularCategories = this.popularCategories();
    if (serviceText) {
      return popularCategories.filter((c) => {
        return c.name.toLowerCase().includes(serviceText.toLowerCase());
      });
    }
    return popularCategories;
  });

  public filteredAllServices() {
    if (this.serviceText()) {
      return this.allServices.filter((c) => {
        return c.name.toLowerCase().includes(this.serviceText().toLowerCase());
      });
    }
    return this.allServices;
  }

  // Fetching Data

  public fetchPopularCategories() {
    const popularCategoriesSub = this.api.getPopularList().subscribe({
      next: (response: any) => {
        this.popularCategories.set(response.data);
        this.cd.markForCheck();
      }
    });
    this.subscriptions.push(popularCategoriesSub);
  }

  public fetchAllServices() {
    const allServicesSub = this.api.getAllServices().subscribe({
      next: (response: any) => {
        this.allServices = response.data.sort((a: any, b: any) => a.name.localeCompare(b.name));
        this.cd.markForCheck();
      }
    });
    this.subscriptions.push(allServicesSub);
  }

  public searchInput() {
    if (this.serviceText() === '') {
      this.searchType.set('');
      this.searchSlug = '';
    }
  }

  public dateChanged(e: any) {
    this.selectedDate.set(moment(e.value));
    this.selectedDateText.set(moment(this.selectedDate()).format('YYYY-MM-DD'));
    this.cd.detectChanges();
  }

  public nativeDateChanged(e: Event) {
    this.selectedDate.set(moment((e.target as HTMLInputElement).value));
    this.selectedDateText.set(moment(this.selectedDate()).format('YYYY-MM-DD'));
    this.cd.detectChanges();
  }

  public tempSearchText = '';

  public openDropdown(dropdown_type: string, element?: any) {
    this.dropdownType.set(dropdown_type);
    if (dropdown_type === 'search' && this.serviceText() === '' && this.tempSearchText) {
      this.serviceText.set(this.tempSearchText);
      this.searchSlug = toUrlSlug(this.serviceText());
    } else if (dropdown_type === 'search') {
      this.tempSearchText = this.serviceText();
      this.serviceText.set('');
    }
    if (element && dropdown_type !== 'calendar') {
      element.value = '';
      this.filteredLocationsList.set([]);
      setTimeout(() => {
        element.focus();
      }, 200);
    }
    if (dropdown_type === '' && this.serviceText() === '' && this.tempSearchText) {
      this.serviceText.set(this.tempSearchText);
      this.searchSlug = toUrlSlug(this.serviceText());
      this.tempSearchText = '';
    }
    this.cd.detectChanges();
  }

  public selectOption(name: string, search_type: string) {
    this.serviceText.set(name);
    this.dropdownType.set('');
    this.searchType.set(search_type);
    this.searchSlug = toUrlSlug(name);
    this.tempSearchText = '';
  }

  public selectBizOption(biz: any, search_type: string) {
    this.serviceText.set(biz.name);
    this.dropdownType.set('');
    this.searchType.set(search_type);
    this.searchSlug = biz.slug;
    this.locationSlug = toUrlSlug(biz.city);
  }

  public selectLocation(location: ILocationDropdownList) {
    this.locationText.set(location.displayText);
    if (this.locationInput) {
      (this.locationInput.nativeElement as HTMLElement).innerText = location.displayText;
    }
    this.dropdownType.set('');
    this.getReverseGeoCodeByAddress(location.prediction);
  }

  public changeToOnline() {
    this.locationText.set('Online');
    this.searchType.set('online');
    this.dropdownType.set('');
  }

  public getmeraLocation() {
    this.store.fetchmeraLocation.next(true);
    this.dropdownType.set('');
  }

  public onLocationTextChanged(e: Event) {
    const inputElement = e.target as HTMLInputElement;
    const locInput = inputElement.value;
    if (locInput === '' || locInput === null) {
      this.clearLocation();
      return;
    }
    this.locationTextChange.next(locInput);
  }

  public clearLocation() {
    this.filteredLocationsList.set([]);
  }

  private getPlaces(value: string): void {
    this.filteredLocationsList.set([]);
    void this.getPlacePredictions(value).then(
      (suggestions) => {
        for (const suggestion of suggestions) {
          const placePrediction = suggestion.placePrediction;
          if (!placePrediction) {
            continue;
          }
          const description = placePrediction.text.text;
          const idx = description.indexOf(', Australia');
          let locationDisplayText = description;
          if (idx !== -1) {
            locationDisplayText = description.substring(0, idx);
          }

          const prediction = new Prediction();
          prediction.description = description;
          prediction.place_id = placePrediction.placeId;
          prediction.structured_formatting = { main_text: placePrediction.mainText?.text ?? locationDisplayText };

          const item = {
            text: description,
            displayText: locationDisplayText,
            place_id: placePrediction.placeId,
            prediction
          };
          this.filteredLocationsList.update((list) => [...list, item]);
        }
        this.cd.markForCheck();
      },
      (error: unknown) => {
        console.info(error);
      }
    );
  }

  private async getPlacePredictions(query: string): Promise<google.maps.places.AutocompleteSuggestion[]> {
    this.filteredLocationsList.set([]);
    await this.googleMapsLoader.whenReady();
    const response = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: query,
      includedPrimaryTypes: ['geocode'],
      includedRegionCodes: ['AU']
    });
    return response.suggestions;
  }

  public toSetLocation = new Location();
  public readonly loading = signal(false);

  private readonly STATE_CAPITALS: Record<string, { city: string; postcode: string; lat: string; lng: string }> = {
    QLD: { city: 'Brisbane', postcode: '4000', lat: '-27.4698', lng: '153.0251' },
    NSW: { city: 'Sydney', postcode: '2000', lat: '-33.8688', lng: '151.2093' },
    VIC: { city: 'Melbourne', postcode: '3000', lat: '-37.8136', lng: '144.9631' },
    WA: { city: 'Perth', postcode: '6000', lat: '-31.9523', lng: '115.8613' },
    SA: { city: 'Adelaide', postcode: '5000', lat: '-34.9285', lng: '138.6007' },
    TAS: { city: 'Hobart', postcode: '7000', lat: '-42.8821', lng: '147.3272' },
    ACT: { city: 'Canberra', postcode: '2600', lat: '-35.2809', lng: '149.13' },
    NT: { city: 'Darwin', postcode: '0800', lat: '-12.4634', lng: '130.8456' }
  };

  public getReverseGeoCodeByAddress(prediction: Prediction) {
    const address = prediction.description;
    this.loading.set(true);
    const responseObs = this.api.getGeoCodingByAddress(address);
    responseObs.subscribe({
      next: (response: any) => {
        this.loading.set(false);
        let state = '';
        let stateCode = '';
        let city = '';
        try {
          let postcode = '';

          if (response.geocode_details.results[0].address_components) {
            for (let i = 0; i < response.geocode_details.results[0].address_components.length; i++) {
              const component = response.geocode_details.results[0].address_components[i];
              if (component.types[0] === 'postal_code') {
                postcode = component.long_name;
              } else if (component.types[0] === 'administrative_area_level_1') {
                state = component.long_name;
                stateCode = component.short_name;
              } else if (
                component.types[0] === 'locality' ||
                (component.types.length > 2 && component.types[1] === 'locality') ||
                (component.types.length > 3 && component.types[2] === 'locality')
              ) {
                city = component.long_name;
              }
            }
          }
          if (postcode === '') {
            prediction.postal_code = this.setMajorCityAndSuburbInfo(city);
          } else {
            prediction.postal_code = postcode;
          }
          const lng = response.geocode_details.results[0].geometry.location.lng;
          const lat = response.geocode_details.results[0].geometry.location.lat;
          prediction.longitude = lng;
          prediction.latitude = lat;

          // State-only selection (e.g. "Queensland") has no postcode/locality and its
          // geometry is the state's centre, so fall back to the state's capital city.
          const capital = this.STATE_CAPITALS[stateCode];
          if (!postcode && !city && capital) {
            // city = capital.city;
            prediction.postal_code = capital.postcode;
            prediction.latitude = capital.lat;
            prediction.longitude = capital.lng;
          }
        } catch (e) {
          console.info(e);
        }

        this.toSetLocation.lat = prediction.latitude;
        this.toSetLocation.lng = prediction.longitude;
        this.toSetLocation.post_code = prediction.postal_code;
        this.toSetLocation.city_name = city;
        this.toSetLocation.state_name = state;

        this.locationSlug = toUrlSlug(city || state);
        this.postCodeSlug = prediction.postal_code;
        this.cd.markForCheck();
      }
    });
  }

  public setMajorCityAndSuburbInfo(suburb: string) {
    const mc = suburb.toLowerCase();
    if (mc === 'brisbane') {
      return '4000';
    } else if (mc === 'perth') {
      return '6000';
    } else if (mc === 'melbourne') {
      return '3000';
    } else if (mc === 'adelaide') {
      return '5000';
    } else if (mc === 'sydney') {
      return '2000';
    }
    return '';
  }

  public searchNow() {
    const browseOrSearch = this.router.url?.split('/')?.[1] || 'browse';
    if (this.searchSlug?.toLowerCase() === 'health and wellbeing services') {
      this.searchSlug = '';
    }
    this.searchSlug = this.searchSlug || 'health-and-wellbeing-services';
    let url = '';
    const queryParams: any = {};
    if (this.searchType() === 'online') {
      url = `/online/${this.searchSlug}`;
    } else {
      if (this.searchType() === 'business') {
        url = `/provider/${this.searchSlug}/${this.locationSlug}`;
      } else if (this.searchType() === 'service') {
        url = `/${browseOrSearch}/${this.searchSlug}/${this.locationSlug}`;
      } else if (this.searchType() === 'health_condition') {
        url = `/${browseOrSearch}/health-condition/${this.searchSlug}/${this.locationSlug}`;
      } else {
        url = `/${browseOrSearch}/health-and-wellbeing-services/${this.locationSlug}`;
      }
      if (this.searchType() !== 'business' && this.searchType() !== 'online' && this.postCodeSlug) {
        url += `/${this.postCodeSlug}`;
      }
      if (this.searchType() !== 'business' && this.selectedDate()) {
        queryParams.dt = this.selectedDate().format('YYYY-MM-DD');
        queryParams.claim_number = this.extra_params.claim_number;
      }
    }

    if (this.toSetLocation?.city_name && this.toSetLocation.state_name) {
      this.store.setmeraLocation(this.toSetLocation);
    }
    this.router.navigate([url], {
      queryParams: queryParams
    });
    this.closeModal();
  }

  public closeModal() {
    this.closePop.emit();
  }

  public locationFocus() {
    this.locationInput.nativeElement.value = '';
  }

  public locationFocusOut(e: Event) {
    if ((e.target as HTMLInputElement).value === '') {
      this.locationInput.nativeElement.value =
        this.store.meraLocation.city_name + ', ' + this.store.meraLocation.state_name;
    }
  }
}
